import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePosts } from '../hooks/usePosts';
import { useJupiterApi } from '../hooks/useJupiterApi';
import CreatePost from '../components/Post/CreatePost';
import PostCard from '../components/Post/PostCard';
import { TokenInfoCard } from '../components/UI/TokenInfoCard';
import { SpaceExplorers } from '../components/UI/SpaceExplorers';
import { RecentActivity } from '../components/UI/RecentActivity';
import { UserProfileModal } from '../components/UI/UserProfileModal';
import { TokenInfo, Post } from '../types';

export const Home: React.FC = () => {
  const { user } = useAuth();
  const { posts, loading, loadingMore, hasMore, createNewPost, toggleLike, addComment, loadMorePosts, onPostCreated } = usePosts();
  const { getTrendingTokens } = useJupiterApi();
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [trendingTokens, setTrendingTokens] = useState<TokenInfo[]>([]);
  const [isCreatePostExpanded, setIsCreatePostExpanded] = useState(false);
  
  // État pour le modal de profil utilisateur
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);

  React.useEffect(() => {
    // Charger les tokens trending pour le sidebar
    const loadSidebarData = async () => {
      try {
        const trending = await getTrendingTokens();
        setTrendingTokens(trending.slice(0, 3)); // Top 3 pour le sidebar
      } catch (error) {
        console.error('Erreur chargement données sidebar:', error);
      }
    };
    loadSidebarData();
  }, [getTrendingTokens]);

  const categories = [
    { value: 'all', label: 'Tous', emoji: '🌟' },
    { value: 'annonces', label: 'Annonces', emoji: '📢' },
    { value: 'feedback', label: 'Feedback', emoji: '💬' },
    { value: 'dao', label: 'DAO', emoji: '🏛️' },
    { value: 'research', label: 'Research', emoji: '🔬' },
    { value: 'meme', label: 'Meme', emoji: '😂' },
    { value: 'launchpad', label: 'Launchpad', emoji: '🚀' }
    
  ];

  const filteredPosts = selectedCategory === 'all' 
    ? posts 
    : posts.filter(post => post.category === selectedCategory);

  const handleUserAvatarClick = (userId: string) => {
    setSelectedUserId(userId);
    setIsProfileModalOpen(true);
  };

  const handleCloseProfileModal = () => {
    setIsProfileModalOpen(false);
    setSelectedUserId('');
  };

  const handleViewFullProfile = (userId: string) => {
    navigate(`/profile/${userId}`);
  };

  const handlePostClick = (post: Post) => {
    navigate(`/post/${post.id}`);
  };

  return (
    <div className="min-h-screen">
      <div className="flex flex-col lg:flex-row gap-4 lg:gap-6 max-w-full lg:max-w-7xl mx-auto px-4">
        {/* Main Feed */}
        <div className="flex-1 lg:max-w-2xl">
          {/* Category Filter - Mode cosmique mobile-friendly */}
          <div className="mb-4 lg:mb-6">
            <div className="flex overflow-x-auto scrollbar-hide gap-2 pb-2">
              {categories.map((category) => (
                <button
                  key={category.value}
                  onClick={() => setSelectedCategory(category.value)}
                  className={`flex-shrink-0 px-3 py-2 lg:px-4 lg:py-2 rounded-full text-sm lg:text-base font-medium transition-all duration-300 cosmic-hover-effect btn-touch ${
                    selectedCategory === category.value
                      ? 'bg-gradient-to-r from-emerald-700 to-cyan-600 text-emerald-50 shadow-cosmic-neon border border-cyan-400/50'
                      : 'bg-gray-800/80 text-emerald-100 hover:bg-emerald-800/30 hover:text-cyan-300 border border-emerald-800/50'
                  }`}
                >
                  <span className="flex items-center gap-1 whitespace-nowrap">
                    <span>{category.emoji}</span>
                    <span className="hidden sm:inline lg:inline">{category.label}</span>
                  </span>
                </button>
              ))}
            </div>
          </div>

        {/* Create Post */}
        {user && (
          <div className="mb-6">
            {/* Seuls les admins peuvent créer un post dans 'annonces' */}
            {selectedCategory === 'annonces' && user.role !== 'admin' ? (
              <div className="cosmic-card p-4 text-center text-emerald-300">
                <span className="text-lg">Seule l'équipe Jupipotes peut publier des annonces importantes.</span>
              </div>
            ) : (
              <CreatePost 
                onPostCreated={onPostCreated} 
                category={selectedCategory === 'all' ? undefined : (selectedCategory as 'feedback' | 'meme' | 'research' | 'launchpad' | 'general' | 'dao' | 'annonces' | undefined)}
                isExpanded={isCreatePostExpanded}
                onToggleExpand={() => setIsCreatePostExpanded(!isCreatePostExpanded)}
              />
            )}
          </div>
        )}

        {/* Posts Feed avec animation de push */}
        <div className={`space-y-4 lg:space-y-6 transition-all duration-700 ease-out transform ${
          isCreatePostExpanded ? 'animate-pushDown' : ''
        }`}>
          {loading && posts.length === 0 ? (
            // Loading skeleton cosmique
            [...Array(3)].map((_, i) => (
              <div key={i} className="cosmic-card p-4 lg:p-6 animate-pulse">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-10 h-10 lg:w-12 lg:h-12 bg-emerald-800/50 rounded-full"></div>
                  <div className="flex-1">
                    <div className="h-4 bg-emerald-800/50 rounded w-32 mb-2"></div>
                    <div className="h-3 bg-emerald-800/50 rounded w-24"></div>
                  </div>
                </div>
                <div className="space-y-2 mb-4">
                  <div className="h-4 bg-emerald-800/50 rounded"></div>
                  <div className="h-4 bg-emerald-800/50 rounded w-3/4"></div>
                </div>
                <div className="flex space-x-6">
                  <div className="h-8 bg-emerald-800/50 rounded w-16"></div>
                  <div className="h-8 bg-emerald-800/50 rounded w-16"></div>
                  <div className="h-8 bg-emerald-800/50 rounded w-16"></div>
                </div>
              </div>
            ))
          ) : filteredPosts.length > 0 ? (
            filteredPosts.map((post) => (
              <PostCard 
                key={post.id} 
                post={post}
                onLike={(postId) => user && toggleLike(postId, user.id)}
                onComment={(postId, content) => user && addComment(postId, content, user.id)}
                onAvatarClick={handleUserAvatarClick}
                onPostClick={handlePostClick}
              />
            ))
          ) : (
            <div className="text-center py-16 cosmic-card">
              <svg className="w-12 h-12 lg:w-16 lg:h-16 text-cyan-400/60 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              <h2 className="text-lg lg:text-xl font-semibold text-emerald-100 mb-2">
                {selectedCategory === 'all' ? 'Aucun post pour le moment' : `Aucun post dans la catégorie ${categories.find(c => c.value === selectedCategory)?.label}`}
              </h2>
              <p className="text-sm lg:text-base text-emerald-200/80">
                {user ? 'Soyez le premier à explorer cet espace !' : 'Connectez-vous pour rejoindre les space cats'}
              </p>
            </div>
          )}
        </div>

        {/* Load More Button cosmique */}
        {filteredPosts.length > 0 && hasMore && (
          <div className="text-center mt-8">
            <button 
              onClick={loadMorePosts}
              disabled={loadingMore}
              className="cosmic-button px-6 py-3 rounded-xl disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loadingMore ? (
                <span className="flex items-center space-x-2">
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Chargement...</span>
                </span>
              ) : (
                'Charger plus de posts'
              )}
            </button>
          </div>
        )}
      </div>

      {/* Right Sidebar - Mode cosmique sticky comme X */}
      <div className="hidden lg:block lg:w-80">
        <div className="sticky top-20 space-y-4 pb-6">
        {/* Trending Tokens cosmique */}
        <div className="cosmic-card p-4">
          <h3 className="text-base font-semibold text-emerald-100 mb-3 flex items-center">
            🔥 <span className="ml-2 neon-text">Tokens Trending</span>
          </h3>
          <div className="space-y-2">
            {trendingTokens.map((token) => (
              <TokenInfoCard 
                key={token.address}
                token={token} 
                compact={true}
              />
            ))}
          </div>
          <button 
            className="w-full mt-3 text-cyan-300 hover:text-cyan-200 text-sm font-medium transition-colors cosmic-hover-effect"
            onClick={() => navigate('/tokens')}
          >
            Voir tous les tokens →
          </button>
        </div>

        {/* Activity Feed */}
        <div className="cosmic-card">
          <RecentActivity showUserActivities={false} maxActivities={5} />
        </div>

        {/* User Stats */}
        {user && (
          <div className="cosmic-card p-4">
            <h3 className="text-base font-semibold text-emerald-100 mb-3">
              📊 <span className="neon-text">Vos Stats</span>
            </h3>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-emerald-200/80">Posts</span>
                <span className="text-emerald-100 font-medium">
                  {posts.filter(p => p.authorId === user.id).length}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-emerald-200/80">Niveau</span>
                <span className="text-cyan-300 font-medium neon-text">{user.level}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-emerald-200/80">XP</span>
                <span className="text-emerald-300 font-medium">{user.xp}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-emerald-200/80">Badges</span>
                <span className="text-cyan-400 font-medium">{user.badges?.length || 0}</span>
              </div>
            </div>
          </div>
        )}
        </div>
      </div>

      {/* Modals avec overlay cosmique */}
      <UserProfileModal 
        isOpen={isProfileModalOpen}
        userId={selectedUserId}
        onClose={handleCloseProfileModal}
        onViewFullProfile={handleViewFullProfile}
      />
      </div>
    </div>
  );
};
